import { useMemo } from 'react';
import { Button } from '@cbhq/cds-web/buttons';
import { SpotRectangle } from '@cbhq/cds-web/illustrations';
import { HStack, Spacer, VStack } from '@cbhq/cds-web/layout';
import {
  Table,
  TableBody,
  TableCell,
  TableCellFallback,
  TableHeader,
  TableRow,
} from '@cbhq/cds-web/tables';
import { TextBody, TextHeadline, TextTitle3 } from '@cbhq/cds-web/typography';

import { GetSmartContractsResponse } from ':cloud/types/service_proto';
import { CoinbaseSmartContract } from ':cloud/types/ts_types';
import { truncateMiddle } from ':cloud/utils/common';
import Permission from ':cloud/widgets/auth';
import { OWNER_ADMIN_ROLES } from ':cloud/widgets/auth/util';
import { ClipboardIcon } from ':cloud/widgets/sharedcomponents';

import { getNetwork } from '../helpers';

function SmartContractTableLoadingState() {
  return (
    <>
      {[...Array(3).keys()].map((e) => (
        <TableRow key={e}>
          <TableCellFallback title />
          <TableCellFallback title />
          <TableCellFallback title />
          <Permission permission={OWNER_ADMIN_ROLES}>
            <TableCellFallback end="image" width={100} />
          </Permission>
        </TableRow>
      ))}
    </>
  );
}

type SmartContractTableEmptyStateProps = {
  onCreatePress: () => void;
};

function SmartContractTableEmptyState({
  onCreatePress,
}: Readonly<SmartContractTableEmptyStateProps>) {
  return (
    <VStack justifyContent="center" alignItems="center" gap={1} width="100%" spacingVertical={10}>
      <VStack width={358} gap={5}>
        <VStack gap={3} alignItems="center">
          <SpotRectangle name="blockchain" scaleMultiplier={1} />
          <TextHeadline as="p" align="center">
            No smart contracts found
          </TextHeadline>
          <Permission permission={OWNER_ADMIN_ROLES}>
            <TextBody as="p" color="foregroundMuted" align="center">
              It takes less than a minute to register one.
            </TextBody>
          </Permission>
        </VStack>
        <Permission permission={OWNER_ADMIN_ROLES}>
          <Button variant="secondary" onPress={onCreatePress}>
            Register Smart Contract
          </Button>
        </Permission>
      </VStack>
    </VStack>
  );
}

type SmartContractTableProps = {
  onCreatePress: () => void;
  onEditPress: (smartContract: CoinbaseSmartContract) => void;
  smartContractsData?: GetSmartContractsResponse;
  isSmartContractsLoading: boolean;
};

export function CoinbaseSmartContractTable({
  onCreatePress,
  onEditPress,
  smartContractsData,
  isSmartContractsLoading,
}: Readonly<SmartContractTableProps>) {
  const isEmpty = (smartContractsData?.data?.length ?? 0) === 0;

  const smartContractTableContents = useMemo(() => {
    return (
      smartContractsData?.data?.map((smartContract, idx) => {
        return (
          <TableRow key={smartContract.smartContractId ?? idx}>
            <TableCell>
              <TextHeadline as="p" spacingVertical={3}>
                {smartContract.contractName}
              </TextHeadline>
            </TableCell>

            <TableCell>
              <HStack gap={1} alignItems="center">
                <VStack background="primaryWash" spacingHorizontal={1}>
                  <TextBody as="p">
                    {truncateMiddle(smartContract.contractAddress, 12, 12, '...')}
                  </TextBody>
                </VStack>
                <ClipboardIcon text={smartContract.contractAddress} iconSize="s" stopPropagation />
              </HStack>
            </TableCell>

            <TableCell>
              <TextBody as="p">{getNetwork(smartContract.networkId)}</TextBody>
            </TableCell>

            <Permission permission={OWNER_ADMIN_ROLES}>
              <TableCell>
                <HStack gap={2}>
                  <Button onPress={() => onEditPress(smartContract)} transparent>
                    Configure
                  </Button>
                </HStack>
              </TableCell>
            </Permission>
          </TableRow>
        );
      }) || []
    );
  }, [smartContractsData, onEditPress]);

  const tableBody = useMemo(() => {
    if (isEmpty && !isSmartContractsLoading) {
      return (
        <TableRow>
          <TableCell colSpan={4}>
            <SmartContractTableEmptyState onCreatePress={onCreatePress} />
          </TableCell>
        </TableRow>
      );
    }
    if (isSmartContractsLoading) {
      return <SmartContractTableLoadingState />;
    }
    return smartContractTableContents;
  }, [isEmpty, isSmartContractsLoading, onCreatePress, smartContractTableContents]);

  return (
    <VStack gap={2}>
      <HStack width="100%" justifyContent="space-between">
        <VStack width="68%" gap={3} spacingBottom={3}>
          <TextTitle3 as="p">Your registered smart contracts</TextTitle3>
          <TextBody as="p" color="foregroundMuted">
            The table below lists smart contracts whose ABIs you have uploaded.
          </TextBody>
        </VStack>
      </HStack>

      <HStack gap={4} justifyContent="space-between">
        <div />
        <Permission permission={OWNER_ADMIN_ROLES}>
          <VStack justifyContent="center">
            <Button onPress={onCreatePress}>+ Register Smart Contract</Button>
          </VStack>
        </Permission>
      </HStack>
      <Spacer />
      <Table tableLayout="auto">
        <TableHeader>
          <TableRow>
            <TableCell title="Contract name" />
            <TableCell title="Contract Address" />
            <TableCell title="Network" />
            <Permission permission={OWNER_ADMIN_ROLES}>
              <TableCell title="" alignItems="center" width={100} />
            </Permission>
          </TableRow>
        </TableHeader>
        <TableBody>{tableBody}</TableBody>
      </Table>
    </VStack>
  );
}
